import type { Answers, Outcome } from './types';

export const OUTCOME_RANK: Record<Outcome, number> = {
  'reading-recovered-closed': 0,
  'do-not-escalate': 0,
  'follow-runbook': 1,
  'escalate-clinic': 2,
  'warm-transfer': 3,
  '911': 4,
};

const anySymptomYes = (answers: Answers): boolean =>
  Object.values(answers.symptoms ?? {}).some(Boolean);

interface ResolveOptions {
  /**
   * Sleeping-mat (Resting Pulse) readings skip the #4 recheck step entirely — no
   * same-call recheck is possible — so `recheck` is always undefined for them. Treat
   * that as "still critical" (the reading never recovered) rather than falling through
   * to `null`, which would leave #6 with no pre-selection or downgrade guard.
   */
  noRecheckStep?: boolean;
}

export const resolveOutcome = (
  answers: Answers,
  options: ResolveOptions = {},
): Outcome | null => {
  if (answers.readingStatus === 'already-recovered') {
    return 'reading-recovered-closed';
  }
  if (answers.contact === 'not-reached') {
    if (answers.unableToReachRunbook === 'do-not-escalate') {
      return 'do-not-escalate';
    }
    if (answers.unableToReachRunbook === 'no-entry') {
      return 'escalate-clinic';
    }
    return null;
  }
  if (answers.consent === 'unable-to-verify') {
    return 'escalate-clinic';
  }
  if (answers.symptoms && Object.keys(answers.symptoms).length > 0) {
    if (anySymptomYes(answers)) return 'warm-transfer';
    // No recheck step (sleeping-mat): no same-call recheck was possible, so the
    // critical reading stands — escalate to clinic.
    if (options.noRecheckStep) return 'escalate-clinic';
    switch (answers.recheck) {
      case 'normal':
        return 'reading-recovered-closed';
      case 'still-critical':
        return 'escalate-clinic';
      case 'at-risk':
      case 'unable-to-recheck':
        if (answers.atRiskRunbook === 'has-entry') return 'follow-runbook';
        if (answers.atRiskRunbook === 'no-entry') return 'warm-transfer';
        return null;
      default:
        return null;
    }
  }
  return null;
};

export const isDowngrade = (from: Outcome, to: Outcome): boolean => {
  if (from === 'warm-transfer' && to === 'escalate-clinic') return false;
  return OUTCOME_RANK[to] < OUTCOME_RANK[from];
};
